// ─────────────────────────────────────────────────────────────────────────────
// localQueue.js — File d'attente locale (IndexedDB) pour les écrits critiques
// terrain. Voir SPEC_file_locale_v1.md (lot 1).
//
// Principe directeur (§3) : rien ne part sur le réseau avant d'avoir été écrit
// sur le disque du terminal. L'entrée locale n'est jamais supprimée par le
// succès : elle est MARQUÉE confirmée, puis deviendra purgeable (§7).
//
// RÈGLE ABSOLUE (§8.1) : une file cassée ne doit JAMAIS empêcher un rapport de
// partir sur le réseau. Toute fonction ci-dessous échoue en silence et ne throw
// jamais vers l'appelant.
//
// Aucune dépendance React — module réutilisable (als7/tc7 plus tard, §2.2).
// ─────────────────────────────────────────────────────────────────────────────

const DB_NAME = 'lc_queue';
const DB_VERSION = 1;
const STORE_ENTRIES = 'entries';
const STORE_BLOBS = 'blobs';
const RETENTION_MS = 30 * 24 * 60 * 60 * 1000; // 30 jours (§7)
const ERR_MAX = 300; // troncature message d'erreur (§4.1 lastError)

// ── Détection IndexedDB (§8.1) ───────────────────────────────────────────────
let _availCache = null;
export function isAvailable() {
  if (_availCache !== null) return _availCache;
  try {
    _availCache = typeof globalThis !== 'undefined'
      && !!globalThis.indexedDB
      && typeof globalThis.indexedDB.open === 'function';
  } catch { _availCache = false; }
  return _availCache;
}

// ── Ouverture (mémoïsée) ─────────────────────────────────────────────────────
let _dbPromise = null;
let _db = null;
function openDb() {
  if (!isAvailable()) return Promise.reject(new Error('IndexedDB indisponible'));
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    let req;
    try { req = globalThis.indexedDB.open(DB_NAME, DB_VERSION); }
    catch (e) { reject(e); return; }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_ENTRIES)) {
        const s = db.createObjectStore(STORE_ENTRIES, { keyPath: 'uid' });
        s.createIndex('by_status', 'status', { unique: false });
        s.createIndex('by_key', 'key', { unique: false });
        s.createIndex('by_createdAt', 'createdAt', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_BLOBS)) {
        db.createObjectStore(STORE_BLOBS, { keyPath: 'blobKey' });
      }
    };
    req.onsuccess = () => { _db = req.result; resolve(req.result); };
    req.onerror = () => reject(req.error || new Error('open failed'));
  });
  // Ne pas garder une promesse rejetée en cache (retentera au prochain appel).
  _dbPromise.catch(() => { _dbPromise = null; });
  return _dbPromise;
}

// Enveloppe une transaction en promesse qui se résout à la fin (oncomplete).
function txDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error || new Error('tx aborted'));
    tx.onerror = () => reject(tx.error || new Error('tx error'));
  });
}
function reqDone(r) {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error || new Error('req error'));
  });
}

function uidGen() {
  return 'q_' + Date.now() + '_' + Math.random().toString(36).slice(2, 9);
}
function trunc(s) {
  const str = (s == null) ? '' : String(s);
  return str.length > ERR_MAX ? str.slice(0, ERR_MAX) : str;
}

// ─────────────────────────────────────────────────────────────────────────────
// enqueue (§5.1, §5.2) — écrit l'entrée AVANT tout réseau, retourne l'uid.
// photos : [{ field:'pk'|'gp', pointId, blob, mime }]  (Blob, pas base64 — §2 #1)
// Retourne l'uid en cas de succès, null si la file n'a pas pu écrire (l'appelant
// poursuit alors le pipeline réseau normalement — aucune régression).
// ─────────────────────────────────────────────────────────────────────────────
export async function enqueue({ key, recordId, payload, photos = [], sid, authorLabel, fin }) {
  if (!isAvailable()) return null;
  const uid = uidGen();
  const nowTs = Date.now();
  const buildEntry = (photoMeta, photosOmises) => ({
    uid, key, recordId,
    payload,
    photos: photoMeta,
    sid: sid || null,
    authorLabel: authorLabel || '',
    createdAt: nowTs,
    lastTryAt: nowTs,
    attempts: 0,
    status: 'pending',
    lastError: '',
    garder: false,
    fin: !!fin,
    ...(photosOmises ? { photosOmises: true } : {}),
  });
  const meta = (photos || [])
    .filter(p => p && p.blob)
    .map(p => ({ field: p.field, pointId: p.pointId, blobKey: `${uid}_${p.field}_${p.pointId}` }));

  const writeAll = async (withPhotos) => {
    const db = await openDb();
    const stores = withPhotos ? [STORE_ENTRIES, STORE_BLOBS] : [STORE_ENTRIES];
    const tx = db.transaction(stores, 'readwrite');
    tx.objectStore(STORE_ENTRIES).put(buildEntry(withPhotos ? meta : [], !withPhotos));
    if (withPhotos) {
      const bs = tx.objectStore(STORE_BLOBS);
      (photos || []).filter(p => p && p.blob).forEach(p => {
        bs.put({
          blobKey: `${uid}_${p.field}_${p.pointId}`,
          blob: p.blob,
          mime: p.mime || (p.blob && p.blob.type) || 'image/jpeg',
          size: (p.blob && p.blob.size) || 0,
        });
      });
    }
    await txDone(tx);
  };

  try {
    await writeAll(true);
    return uid;
  } catch (e1) {
    // Quota dépassé (§8.2) : purger, réessayer une fois, puis écrire sans photos.
    const quota = e1 && (e1.name === 'QuotaExceededError'
      || /quota/i.test(e1.message || ''));
    if (quota) {
      try { await purgeExpired(); } catch { /* ignore */ }
      try { await writeAll(true); return uid; } catch { /* fallthrough */ }
      try { await writeAll(false); return uid; } catch { /* fallthrough */ } // texte sans photos > rien
    }
    console.error('[localQueue] enqueue échoué (non bloquant)', e1);
    return null;
  }
}

// Mutation d'une entrée existante par uid (patch partiel). Silencieuse.
async function patchEntry(uid, patch) {
  if (!isAvailable() || !uid) return false;
  try {
    const db = await openDb();
    const tx = db.transaction([STORE_ENTRIES], 'readwrite');
    const store = tx.objectStore(STORE_ENTRIES);
    const cur = await reqDone(store.get(uid));
    if (!cur) { tx.abort(); return false; }
    store.put({ ...cur, ...patch });
    await txDone(tx);
    return true;
  } catch (e) {
    console.error('[localQueue] patchEntry échoué', e);
    return false;
  }
}

export function markConfirmed(uid) {
  return patchEntry(uid, { status: 'confirmed', lastTryAt: Date.now() });
}
export function markFailed(uid, error) {
  return patchEntry(uid, { status: 'failed', lastError: trunc(error), lastTryAt: Date.now() });
}
export async function touch(uid, error) {
  if (!isAvailable() || !uid) return false;
  try {
    const db = await openDb();
    const tx = db.transaction([STORE_ENTRIES], 'readwrite');
    const store = tx.objectStore(STORE_ENTRIES);
    const cur = await reqDone(store.get(uid));
    if (!cur) { tx.abort(); return false; }
    store.put({
      ...cur,
      attempts: (cur.attempts || 0) + 1,
      lastTryAt: Date.now(),
      ...(error !== undefined ? { lastError: trunc(error) } : {}),
    });
    await txDone(tx);
    return true;
  } catch (e) {
    console.error('[localQueue] touch échoué', e);
    return false;
  }
}

export function setGarder(uid, bool) {
  return patchEntry(uid, { garder: !!bool });
}

// ── Confirmation par recordId (§5.3) ─────────────────────────────────────────
// Après un 200 sur `key`, marque confirmées les entrées `pending` de cette clé
// dont le recordId figure dans la valeur réellement écrite. Le contrôle par
// recordId évite de confirmer une entrée absente du payload transmis.
export async function confirmByRecordIds(key, recordIds) {
  if (!isAvailable() || !key || !Array.isArray(recordIds) || recordIds.length === 0) return 0;
  const ids = new Set(recordIds.filter(Boolean).map(String));
  try {
    const db = await openDb();
    const tx = db.transaction([STORE_ENTRIES], 'readwrite');
    const store = tx.objectStore(STORE_ENTRIES);
    const all = await reqDone(store.getAll());
    let n = 0;
    all.forEach(e => {
      if (e.key === key && e.status === 'pending' && ids.has(String(e.recordId))) {
        store.put({ ...e, status: 'confirmed', lastTryAt: Date.now() });
        n++;
      }
    });
    await txDone(tx);
    return n;
  } catch (e) {
    console.error('[localQueue] confirmByRecordIds échoué', e);
    return 0;
  }
}

// ── Lectures (sans les blobs — §4.2) ─────────────────────────────────────────
function stripBlobs(e) {
  // On ne renvoie jamais les Blob dans les listes : ils vivent dans STORE_BLOBS.
  const { ...rest } = e;
  return rest;
}
export async function listPending() {
  if (!isAvailable()) return [];
  try {
    const db = await openDb();
    const tx = db.transaction([STORE_ENTRIES], 'readonly');
    const all = await reqDone(tx.objectStore(STORE_ENTRIES).getAll());
    return all.filter(e => e.status === 'pending').map(stripBlobs);
  } catch (e) {
    console.error('[localQueue] listPending échoué', e);
    return [];
  }
}
export async function listAll({ includeConfirmed = false } = {}) {
  if (!isAvailable()) return [];
  try {
    const db = await openDb();
    const tx = db.transaction([STORE_ENTRIES], 'readonly');
    const all = await reqDone(tx.objectStore(STORE_ENTRIES).getAll());
    return all
      .filter(e => e.status !== 'confirmed' || includeConfirmed)
      .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
      .map(stripBlobs);
  } catch (e) {
    console.error('[localQueue] listAll échoué', e);
    return [];
  }
}

// getBlobs (à la demande, au rejeu uniquement — §5.1) : reconstruit la liste des
// photos avec leur Blob pour ré-uploader.
export async function getBlobs(uid) {
  if (!isAvailable() || !uid) return [];
  try {
    const db = await openDb();
    const tx = db.transaction([STORE_ENTRIES, STORE_BLOBS], 'readonly');
    const entry = await reqDone(tx.objectStore(STORE_ENTRIES).get(uid));
    if (!entry || !Array.isArray(entry.photos)) return [];
    const bs = tx.objectStore(STORE_BLOBS);
    const out = [];
    for (const p of entry.photos) {
      const rec = await reqDone(bs.get(p.blobKey));
      if (rec) out.push({ field: p.field, pointId: p.pointId, blob: rec.blob, mime: rec.mime });
    }
    return out;
  } catch (e) {
    console.error('[localQueue] getBlobs échoué', e);
    return [];
  }
}

// ── Suppression (avec ses blobs, même transaction — §7.1) ────────────────────
export async function remove(uid) {
  if (!isAvailable() || !uid) return false;
  try {
    const db = await openDb();
    const tx = db.transaction([STORE_ENTRIES, STORE_BLOBS], 'readwrite');
    const store = tx.objectStore(STORE_ENTRIES);
    const entry = await reqDone(store.get(uid));
    store.delete(uid);
    if (entry && Array.isArray(entry.photos)) {
      const bs = tx.objectStore(STORE_BLOBS);
      entry.photos.forEach(p => bs.delete(p.blobKey));
    }
    await txDone(tx);
    return true;
  } catch (e) {
    console.error('[localQueue] remove échoué', e);
    return false;
  }
}

// ── Purge (§7) ───────────────────────────────────────────────────────────────
// confirmed uniquement, createdAt > 30 j, garder !== true. pending/failed JAMAIS.
// Supprime les blobs associés dans la même transaction. Retourne le nb purgé.
export async function purgeExpired() {
  if (!isAvailable()) return 0;
  try {
    const db = await openDb();
    const tx = db.transaction([STORE_ENTRIES, STORE_BLOBS], 'readwrite');
    const store = tx.objectStore(STORE_ENTRIES);
    const bs = tx.objectStore(STORE_BLOBS);
    const all = await reqDone(store.getAll());
    const cutoff = Date.now() - RETENTION_MS;
    let n = 0;
    all.forEach(e => {
      if (e.status === 'confirmed' && e.garder !== true && (e.createdAt || 0) < cutoff) {
        store.delete(e.uid);
        if (Array.isArray(e.photos)) e.photos.forEach(p => bs.delete(p.blobKey));
        n++;
      }
    });
    await txDone(tx);
    return n;
  } catch (e) {
    console.error('[localQueue] purgeExpired échoué', e);
    return 0;
  }
}

// Vide les deux stores (tests uniquement).
export async function __clearForTests() {
  if (!isAvailable()) return;
  try {
    const db = await openDb();
    const tx = db.transaction([STORE_ENTRIES, STORE_BLOBS], 'readwrite');
    tx.objectStore(STORE_ENTRIES).clear();
    tx.objectStore(STORE_BLOBS).clear();
    await txDone(tx);
  } catch { /* ignore */ }
}

// Réinitialisation du cache (tests / HMR). Non exporté vers l'app en prod.
export function __resetForTests() {
  try { if (_db) _db.close(); } catch { /* ignore */ }
  _db = null;
  _dbPromise = null;
  _availCache = null;
}

// ── Convenience rapport (§5.2) ───────────────────────────────────────────────
// Convertit pk/gp (objets {pointId:dataUrl}) en Blob puis enfile le rapport.
// Appelé par les DEUX chemins de soumission (mode guidé via rapportCore ET
// RapportModal classique) : un seul endroit pour qu'ils restent synchronisés,
// ce qui neutralise le risque décrit dans SPEC §10.1 (double point d'insertion).
export async function enqueueRapport({ recordId, payload, pk, gp, sid, authorLabel, fin }) {
  if (!isAvailable()) return null;
  const photos = [];
  const conv = async (obj, field) => {
    for (const [pointId, v] of Object.entries(obj || {})) {
      if (typeof v === 'string' && v.startsWith('data:image')) {
        try {
          const blob = await (await fetch(v)).blob(); // même méthode que uploadDataUrlToStorage
          photos.push({ field, pointId, blob, mime: blob.type || 'image/jpeg' });
        } catch (e) { console.error('[localQueue] conversion photo échouée', field, pointId, e); }
      }
    }
  };
  try { await conv(pk, 'pk'); await conv(gp, 'gp'); }
  catch (e) { console.error('[localQueue] enqueueRapport conversion', e); }
  return enqueue({ key: 'rep7', recordId, payload, photos, sid, authorLabel, fin });
}

// ── Convenience contrôle qualité (Chantier B) ────────────────────────────────
// Même principe que enqueueRapport : convertit les photos (base64) en Blob avant
// mise en file. Structure différente de rep7 : plusieurs photos PAR PIÈCE
// (payload.pieces[pieceId].photos = [dataUrl, dataUrl, ...]), d'où l'encodage
// du pointId en "pieceId__index" pour pouvoir tout reconstruire à l'identique
// au rejeu (voir reconstructControlePhotos ci-dessous, utilisé par le rejeu
// silencieux dans queueAutoReplay.js).
// Appelée UNE SEULE FOIS, à l'intérieur de CtrlModal (SecRapports.jsx), pour
// que les 3 sites d'appel (SecControles, StaffUI, GerantUI) en bénéficient
// sans dupliquer l'appel trois fois.
export async function enqueueControle({ recordId, payload, sid, authorLabel, fin }) {
  if (!isAvailable()) return null;
  const photos = [];
  const leanPieces = {};
  try {
    for (const [pieceId, p] of Object.entries((payload && payload.pieces) || {})) {
      const list = (p && p.photos) || [];
      for (let idx = 0; idx < list.length; idx++) {
        const v = list[idx];
        if (typeof v === 'string' && v.startsWith('data:image')) {
          try {
            const blob = await (await fetch(v)).blob();
            photos.push({ field: 'piece', pointId: `${pieceId}__${idx}`, blob, mime: blob.type || 'image/jpeg' });
          } catch (e) { console.error('[localQueue] conversion photo contrôle échouée', pieceId, idx, e); }
        }
      }
      // payload allégé (même principe que rep7/pk-gp) : la longueur du tableau est
      // conservée pour reconstruire aux bons index, son contenu est vidé — les
      // vraies données vivent dans les Blobs, pas dupliquées dans le payload.
      leanPieces[pieceId] = { ...p, photos: new Array(list.length).fill(null) };
    }
  } catch (e) { console.error('[localQueue] enqueueControle conversion', e); }
  const leanPayload = { ...payload, pieces: leanPieces };
  return enqueue({ key: 'controles7', recordId, payload: leanPayload, photos, sid, authorLabel, fin });
}

// Reconstruit payload.pieces[pieceId].photos à partir des Blobs stockés (rejeu).
// Ne touche jamais aux pièces qui n'avaient pas de photo (dataUrl déjà absente
// ou vide) — reconstruction additive uniquement, jamais destructive.
export async function reconstructControlePhotos(entry) {
  const payload = entry && entry.payload;
  if (!payload || !Array.isArray(entry.photos) || entry.photos.length === 0) return payload;
  const blobToDataUrl = (blob) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = () => rej(r.error || new Error('read'));
    r.readAsDataURL(blob);
  });
  const blobs = await getBlobs(entry.uid);
  const pieces = { ...(payload.pieces || {}) };
  for (const b of blobs) {
    if (b.field !== 'piece') continue;
    const sep = b.pointId.lastIndexOf('__');
    if (sep < 0) continue;
    const pieceId = b.pointId.slice(0, sep);
    const idx = Number(b.pointId.slice(sep + 2));
    if (!pieces[pieceId]) continue;
    try {
      const durl = await blobToDataUrl(b.blob);
      const photosArr = Array.isArray(pieces[pieceId].photos) ? [...pieces[pieceId].photos] : [];
      photosArr[idx] = durl;
      pieces[pieceId] = { ...pieces[pieceId], photos: photosArr };
    } catch (e) { console.error('[localQueue] reconstruction photo contrôle échouée', b.pointId, e); }
  }
  return { ...payload, pieces };
}
